import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  subscriptionUpgradeApi,
  subscriptionUpgradeQueryKeys,
  type UpgradeQuote,
} from "../../../shared/api/subscription";
import { apiError } from "../../../shared/api/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const VND = new Intl.NumberFormat("vi-VN");
const d = (n: number) => `${VND.format(n)}đ`;

/**
 * Nâng gói giữa kỳ (#18990).
 *
 * <p>Số tiền hiện ở đây do SERVER tính và đã đông cứng. Màn hình không tự tính lại — tính lại ở
 * client là cách nhanh nhất để con số hiện ra khác con số bị thu, và khoảng cách giữa chúng đúng
 * bằng thời gian người dùng ngồi đọc.
 *
 * <p>Mọi con số phụ (chênh lệch toàn kỳ, thời gian còn lại) có mặt để GIẢI THÍCH con số chính, chứ
 * không bắt người dùng tự nhân chia.
 */
export function UpgradePlanCard({ targetPlanCode }: { targetPlanCode: string }) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const open = useQuery({
    queryKey: subscriptionUpgradeQueryKeys.open(),
    queryFn: subscriptionUpgradeApi.open,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["subscription-upgrade"] });

  const quote = useMutation({
    mutationFn: () => subscriptionUpgradeApi.quote(targetPlanCode),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (e) => setError(apiError(e).detail),
  });

  const payWallet = useMutation({
    mutationFn: (q: UpgradeQuote) => subscriptionUpgradeApi.payFromWallet(q.intentId),
    onSuccess: () => {
      setDone(true);
      setError(null);
      qc.invalidateQueries({ queryKey: ["subscription"] });
      refresh();
    },
    onError: (e) => setError(apiError(e).detail),
  });

  const paySePay = useMutation({
    mutationFn: (q: UpgradeQuote) => subscriptionUpgradeApi.paySePay(q.intentId),
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: ["saas-payment"] });
      refresh();
    },
    onError: (e) => setError(apiError(e).detail),
  });

  if (done) {
    return (
      <Card>
        <CardContent className="p-5 text-sm">
          Đã nâng lên <b>{targetPlanCode}</b>. Gói mới có hiệu lực ngay, và ngày kết thúc kỳ hiện
          tại không đổi.
        </CardContent>
      </Card>
    );
  }

  const q = open.data;

  return (
    <Card>
      <CardContent className="space-y-4 p-5">
        <h3 className="text-sm font-semibold">Nâng lên gói {targetPlanCode}</h3>

        {!q && (
          <>
            <p className="text-sm text-muted-foreground">
              Gói mới có hiệu lực <b>ngay</b>. Chỉ trả phần chênh cho thời gian còn lại của kỳ hiện
              tại — ngày kết thúc kỳ không đổi.
            </p>
            <Button size="sm" disabled={quote.isPending} onClick={() => quote.mutate()}>
              {quote.isPending ? "Đang tính…" : "Xem số tiền phải trả"}
            </Button>
          </>
        )}

        {q && (
          <>
            <dl className="space-y-1 text-sm">
              <Row label="Gói hiện tại" value={`${q.fromPlanCode} · ${d(q.fromPriceVnd)}/tháng`} />
              <Row label="Gói mới" value={`${q.toPlanCode} · ${d(q.toPriceVnd)}/tháng`} />
              <Row label="Chênh lệch toàn kỳ" value={d(q.fullDifferenceVnd)} />
              <Row
                label="Thời gian còn lại"
                value={`${remaining(q)} · tới ${new Date(q.periodEndsAt).toLocaleDateString("vi-VN")}`}
              />
            </dl>
            <div className="rounded-md bg-primary/10 px-3 py-2">
              <div className="text-xs text-muted-foreground">Thanh toán thêm</div>
              <strong className="text-lg tabular-nums text-primary">{d(q.amountVnd)}</strong>
            </div>

            {error && (
              <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </p>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                disabled={!q.walletCovers || payWallet.isPending}
                onClick={() => payWallet.mutate(q)}
              >
                {payWallet.isPending ? "Đang xử lý…" : "Thanh toán bằng số dư"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={paySePay.isPending}
                onClick={() => paySePay.mutate(q)}
              >
                {paySePay.isPending ? "Đang tạo…" : "Thanh toán qua SePay"}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => subscriptionUpgradeApi.cancel(q.intentId).then(refresh)}
              >
                Huỷ
              </Button>
            </div>
            {!q.walletCovers && (
              <p className="text-xs text-muted-foreground">
                Số dư ví {d(q.walletBalanceVnd)} — chưa đủ. Hãy nạp thêm hoặc trả qua SePay.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

/** Thời gian còn lại, nói bằng tiếng người: "20 ngày", không phải "1728000 giây". */
function remaining(q: UpgradeQuote): string {
  const days = Math.floor(q.remainingSeconds / 86400);
  if (days >= 1) {
    const total = Math.round(q.totalSeconds / 86400);
    return `${days}/${total} ngày`;
  }
  const hours = Math.max(1, Math.round(q.remainingSeconds / 3600));
  return `${hours} giờ`;
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right font-medium">{value}</dd>
    </div>
  );
}
